import React, { useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import CustomDatePicker from "../../../components/CustomDatePicker";
import swal from "sweetalert";
import { validateCheckNumbers } from "../../../utils/checkNumberValidator";
import CheckNumberError from "../../../components/inputs/CheckNumberError";
import { selectCustomStyles } from "../../../assets/global/selectCustomStyles";
import Select from "react-select";
import {
  currencySymbol,
  formatToTwoDecimal,
  parseNumber,
  sanitizeAmount,
  truncateToTwoDecimals,
} from "../../../utils/numberFormatter";
import axios from "axios";
import BASE_URL from "../../../assets/global/url";
import useDecodeToken from "../../../hooks/customHook/useDecodeToken";
import { checkCutoffDate } from "../../../utils/cutoffDateValidation";

const FixedAssetPaymentModal = ({
  fixedAssetInfo,
  showPaymentModal,
  handleClosePaymentModal,
  CustomInput,
  currencyId,
  expensesValue,
  fetchFixedAsset, // To refresh data after payment submission
}) => {
  // Remaining expenses value, used as the default payment amount
  const getRemainingAmount = () =>
    Math.max(Number(expensesValue) || 0, 0).toFixed(2);

  // Default form values: process date is today and maturity date follows it,
  // amount is the remaining expenses value
  const getInitialForm = () => {
    const today = new Date();

    return {
      paymentMethod: "Bank",
      issuedDate: today,
      processDate: today,
      checkNumber: "",
      subject1: "",
      subject2: "",
      subject3: "",
      amount: getRemainingAmount(),
    };
  };

  const [paymentForm, setPaymentForm] = useState(getInitialForm);

  const isCash = paymentForm.paymentMethod === "Cash";

  // Symbol of the fixed asset's currency, shown beside the amount input
  const amountCurrencySymbol =
    currencySymbol[fixedAssetInfo?.currencyName] || currencySymbol.PHP;

  // For form validation
  const [validated, setValidated] = useState(false);

  // For options
  const [subject2List, setSubject2List] = useState([]);
  const [subject3List, setSubject3List] = useState([]);

  // Access type (Management/Employee) of the logged in user, used to decide
  // whether the account balance is shown on this modal.
  const userLoggedID = useDecodeToken();
  const [accessType, setAccessType] = useState("");

  // Centralized fixed asset API endpoints
  const fixedAssetApi = {
    subject2: {
      get: ({ config }) => axios.get(`${BASE_URL}/fixedasset/subject2`, config),
    },
    subject3: {
      get: ({ currencyId, config }) =>
        axios.get(`${BASE_URL}/fixedasset/subject3`, {
          params: {
            currencyId,
          },
          ...config,
        }),
    },
    payment: {
      post: ({ payload, config }) =>
        axios.post(`${BASE_URL}/fixedasset/payment`, payload, config),
    },
    // Not a fixed asset endpoint; kept here so it goes through handleApi
    accessType: {
      get: ({ userId, config }) =>
        axios.get(`${BASE_URL}/userRole/getUserAccessType/${userId}`, config),
    },
  };

  // Handle api requests
  const handleApi = async ({ apiFn, ...args }) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000); // Automatically abort request after timeout

    try {
      const response = await apiFn({
        ...args,
        config: { signal: controller.signal },
      });
      return response;
    } catch (error) {
      console.error(error);
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  };

  // Dropdown options for subject 1
  const subject1Options = [
    // "Owner's Equity Account",
    "Account-List",
    "Asset Account",
    // "Liabilities Account",
  ].map((item) => ({
    value: item,
    label: item,
    isDisabled:
      item === "Owner's Equity Account" || item === "Liabilities Account",
  }));

  // Dropdown options for subject 2
  const filteredSubject2Options = subject2List
    .filter(
      (item) =>
        item.module_type === paymentForm?.subject1 &&
        item.subject_type === paymentForm?.paymentMethod,
    )
    .map((item) => ({
      value: item.id,
      label: item.subject_name,
    }));

  // Dropdown options for subject 3
  const filteredSubject3Options = subject3List
    ?.filter((item) => item.account_list_base_sub_id === paymentForm?.subject2)
    .map((item) => ({
      value: item.id,
      label: item.account_name,
    }));

  // Currently selected account (subject 3), used to show its balance
  const selectedAccount = subject3List?.find(
    (item) => String(item.id) === String(paymentForm?.subject3),
  );

  // Helper: Find the option object that matches the given value
  const findOption = (options, compareValue) =>
    options?.find((item) => item.value === compareValue) || "";

  // Helper: Automatically add comma to number
  const addCommaToNumber = (value) => {
    if (!value) return "";

    const parts = String(value)?.split(".");
    parts[0] = parts[0]?.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    return parts?.join(".");
  };

  // Dependency look up function
  const getFieldDependencies = (field) => {
    const dependency = {
      paymentMethod: {
        subject1: "",
        subject2: "",
        subject3: "",
        checkNumber: "",
      },
      subject1: {
        subject2: "",
        subject3: "",
      },
      subject2: {
        subject3: "",
      },
    };

    return dependency[field] ?? {};
  };

  // Handle form input change
  const handleChange = (value, field) => {
    const resetFields = getFieldDependencies(field);

    setPaymentForm((prev) => {
      const updated = {
        ...prev,
        ...resetFields,
        [field]: value,
      };

      // Maturity date follows the process date when the process date changes,
      // and is always the same as the process date for Cash
      if (field === "processDate" || updated.paymentMethod === "Cash") {
        updated.issuedDate = updated.processDate;
      }

      return updated;
    });
  };

  // Setter for the process date picker's "Today" button, which writes through
  // the setter directly; keeps the maturity date in sync like handleChange
  const setProcessDateForm = (updater) =>
    setPaymentForm((prev) => {
      const updated = typeof updater === "function" ? updater(prev) : updater;
      return { ...updated, issuedDate: updated.processDate };
    });

  // Handle account (subject 3) selection, automatically fills in the amount
  // with the remaining expenses value
  const handleSubject3Change = (option) => {
    handleChange(option.value, "subject3");
    handleChange(getRemainingAmount(), "amount");
  };

  // Handle Amount Validation and onChange
  const handleAmountChange = (e) => {
    const rawValue = e.target.value;
    const sanitizedValue = sanitizeAmount(rawValue);
    const numericValue = parseNumber(sanitizedValue);

    if (numericValue > expensesValue) {
      swal({
        icon: "warning",
        title: "Amount Exceeded",
        text: `The payment amount cannot exceed the remaining expenses value of ${formatToTwoDecimal(expensesValue)}.`,
      });

      handleChange(expensesValue.toString(), "amount");
    } else {
      handleChange(sanitizedValue, "amount");
    }
  };

  // Handle form submit
  const handleSubmit = async (event) => {
    setValidated(true);

    event.preventDefault();
    event.stopPropagation();
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      swal({
        icon: "error",
        title: "Fields are required.",
        text: "Please fill in all required fields.",
      });
      return;
    }

    if (
      paymentForm.paymentMethod === "Bank" &&
      paymentForm.checkNumber &&
      !(await validateCheckNumbers(paymentForm.checkNumber))
    )
      return;

    const confirmation = await swal({
      icon: "warning",
      title: "Confirm Payment",
      text: "This action will process the payment. Are you sure you want to continue?",
      buttons: ["Cancel", "Yes"],
      dangerMode: true,
    });

    if (!confirmation) return;

    try {
      const response = await handleApi({
        apiFn: fixedAssetApi.payment.post,
        payload: {
          fixedAssetInfo,
          paymentForm: {
            ...paymentForm,
            amount: parseNumber(paymentForm.amount),
          },
          // A Management user's Bank payment (no check number) is Confirmed
          // and deducted right away
          userLoggedID,
        },
      });

      if (response.status === 200) {
        swal({
          icon: "success",
          title: "Successful",
          text: "Payment has been successfully created.",
        });
      }
    } catch (error) {
      console.error(error);

      if (
        error.response?.status === 400 &&
        error.response.data.message?.includes("Insufficient Balance")
      ) {
        swal({
          icon: "error",
          title: "Not Enough Balance",
          text: error.response.data.message,
        });
        return;
      }

      swal({
        title: "Something went wrong",
        text: "Please contact your support immediately",
        icon: "error",
      });
    } finally {
      handleClosePaymentModal(clearFields);
      fetchFixedAsset(fixedAssetInfo.id);
    }
  };

  // Clear all input fields in the payment form and reset validation
  const clearFields = () => {
    setPaymentForm(getInitialForm());

    setValidated(false);
  };

  const fetchData = async () => {
    try {
      const [subject2, subject3] = await Promise.all([
        handleApi({ apiFn: fixedAssetApi.subject2.get }),
        handleApi({ apiFn: fixedAssetApi.subject3.get, currencyId }),
      ]);

      setSubject2List(subject2.data);
      setSubject3List(subject3.data);
    } catch (error) {
      swal({
        title: "Something went wrong",
        text: "Please contact your support immediately",
        icon: "error",
      });

      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currencyId]);

  // Refresh the accounts every time the modal is opened so the displayed
  // account balance is never stale (e.g. right after a previous payment)
  useEffect(() => {
    if (showPaymentModal) fetchData();
  }, [showPaymentModal]);

  // Default the amount to the latest remaining expenses value on open
  // (expensesValue changes after each payment, so it can't only be set once)
  useEffect(() => {
    if (showPaymentModal) {
      setPaymentForm((prev) => ({ ...prev, amount: getRemainingAmount() }));
    }
  }, [showPaymentModal, expensesValue]);

  // Fetch the access type (Management/Employee) of the logged in user
  const fetchAccessType = async () => {
    try {
      const response = await handleApi({
        apiFn: fixedAssetApi.accessType.get,
        userId: userLoggedID,
      });

      setAccessType(response.data.accessType);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (userLoggedID) fetchAccessType();
  }, [userLoggedID]);

  return (
    <Modal
      show={showPaymentModal}
      onHide={() => handleClosePaymentModal(clearFields)}
      backdrop="static"
    >
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title className="text-primary fw-semibold">
            Fixed Asset Payment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3">
          {/* Payment Method */}
          <div>
            <div className="fw-semibold mb-2">Pay With</div>
            <div className="d-flex gap-3">
              <Form.Check
                inline
                label="Bank"
                name="paymentMethod"
                type={"radio"}
                value="Bank"
                checked={paymentForm.paymentMethod === "Bank"}
                onChange={(e) => handleChange(e.target.value, "paymentMethod")}
                className="custom-radio"
              />

              <Form.Check
                inline
                label="Cash"
                name="paymentMethod"
                type={"radio"}
                value="Cash"
                checked={paymentForm.paymentMethod === "Cash"}
                onChange={(e) => handleChange(e.target.value, "paymentMethod")}
                className="custom-radio"
              />
            </div>
          </div>

          {/* Subject 1 */}
          <div>
            <label className="w-100">
              <span className="fw-semibold">
                Subject 1 <span className="text-danger small"> *</span>
              </span>
              <Select
                options={subject1Options}
                value={findOption(subject1Options, paymentForm?.subject1)}
                onChange={(option) => handleChange(option.value, "subject1")}
                placeholder={`Select Subject 1`}
                styles={selectCustomStyles(paymentForm.subject1, validated)}
                required
              />
            </label>
          </div>

          {/* Subject 2 */}
          <div>
            <label className="w-100">
              <span className="fw-semibold">
                Subject 2 <span className="text-danger small"> *</span>
              </span>
              <Select
                options={filteredSubject2Options}
                value={findOption(
                  filteredSubject2Options,
                  paymentForm.subject2,
                )}
                onChange={(option) => handleChange(option.value, "subject2")}
                placeholder={`Select Subject 2`}
                styles={selectCustomStyles(paymentForm.subject2, validated)}
                isDisabled={!paymentForm.subject1}
                required
              />
            </label>
          </div>

          {/* Subject 3 */}
          <div>
            <label className="w-100">
              <span className="fw-semibold">
                Subject 3 <span className="text-danger small"> *</span>
              </span>
              <Select
                options={filteredSubject3Options}
                value={findOption(
                  filteredSubject3Options,
                  paymentForm.subject3,
                )}
                onChange={handleSubject3Change}
                placeholder={`Select Subject 3`}
                styles={selectCustomStyles(paymentForm.subject3, validated)}
                isDisabled={!paymentForm.subject2}
                required
              />
            </label>
          </div>

          {/* Kung kailang na register or na create ang bayad -- para to sa final balance sheet since ang basihan is kailan nagawa ang transaction */}
          <div>
            <label htmlFor="transaction-date" className="fw-semibold">
              Process Date <span className="text-danger small"> *</span>
            </label>
            <CustomDatePicker
              selected={
                paymentForm.processDate ? new Date(paymentForm.processDate) : ""
              }
              handleDateChange={(date) => {
                handleChange(date, "processDate");
                checkCutoffDate(date, "Process Date"); // warning only
              }}
              setter={setProcessDateForm}
              field={"processDate"}
              CustomInput={CustomInput}
              isRequired={true}
              validated={validated}
              // "Today" button
              dateValidation={(today) => {
                checkCutoffDate(today, "Process Date");
              }}
              iconTopOffset={"0.8rem"}
              padding={"p-2"}
            />
          </div>

          {/* Issued Date */}
          {/* Kung kailan ma pay ang bayad */}
          {/* Hidden for Cash: maturity date is the same as the process date */}
          {!isCash && (
            <div>
              <label htmlFor="transaction-date" className="fw-semibold">
                Maturity Date <span className="text-danger small"> *</span>
              </label>
              <CustomDatePicker
                selected={
                  paymentForm.issuedDate ? new Date(paymentForm.issuedDate) : ""
                }
                handleDateChange={(date) => {
                  handleChange(date, "issuedDate");
                  checkCutoffDate(date, "Maturity Date"); // warning only
                }}
                setter={setPaymentForm}
                field={"issuedDate"}
                CustomInput={CustomInput}
                isRequired={true}
                validated={validated}
                // "Today" button
                dateValidation={(today) => {
                  checkCutoffDate(today, "Maturity Date");
                }}
                iconTopOffset={"0.8rem"}
                padding={"p-2"}
              />
            </div>
          )}

          {/* Check Number (Bank only) */}
          {!isCash && (
            <div>
              <label
                htmlFor="payment-check-number"
                className="w-100 fw-semibold"
              >
                Check Number
              </label>
              <input
                type="text"
                value={paymentForm.checkNumber}
                onChange={(e) => handleChange(e.target.value, "checkNumber")}
                name="payment-check-number"
                className="form-control"
                id="payment-check-number"
              />
              <CheckNumberError value={paymentForm.checkNumber} />
            </div>
          )}

          {/* Amount */}
          <div>
            <label htmlFor="payment-amount" className="w-100 fw-semibold">
              Amount <span className="text-danger small"> *</span>
            </label>

            <div className="input-group">
              <span className="input-group-text">{amountCurrencySymbol}</span>
              <input
                type="text"
                value={addCommaToNumber(paymentForm?.amount)}
                // onChange={(e) =>
                //   handleChange(sanitizeAmount(e.target.value), "amount")
                // }
                onChange={handleAmountChange}
                name="payment-amount"
                className="form-control"
                id="payment-amount"
                required
              />
            </div>
            {accessType === "Management" && selectedAccount && (
              <span>
                Account Balance:{" "}
                <strong>
                  {amountCurrencySymbol}{" "}
                  {truncateToTwoDecimals(Number(selectedAccount.amount) || 0)}
                </strong>
              </span>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="submit" className="btn btn-primary">
            Save
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default FixedAssetPaymentModal;
